import * as THREE from 'three';
import { TICK_HZ } from '@somnhold/content';
import { gladeUp, hexToWorld, KNOT, NONE, onMound, thawWet, type BattleState } from '@somnhold/sim';
import { simToWorld } from '@somnhold/render';
import { pinMark, remeasureMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';

/** A wave of water. */
const WATER = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 6.5c1.6-1.4 3.4-1.4 5 0s3.4 1.4 5 0 2.4-1 3 0M1.5 10.5c1.6-1.4 3.4-1.4 5 0s3.4 1.4 5 0 2.4-1 3 0" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
/** A mound: a hump with a sapling on it. */
const MOUND = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 13.5c1.6-4 4-5.5 6.5-5.5s4.9 1.5 6.5 5.5z" fill="currentColor"/><path d="M8 8V3.5M8 5.5l2-1.5M8 6.5L6 5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
/** A shoot: two new leaves. */
const SHOOT = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 14V7" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M8 8C5 8 3.5 6 3.5 3.5 6.5 3.5 8 5.5 8 8zM8 9c3 0 4.5-2 4.5-4.5C9.5 4.5 8 6.5 8 9z" fill="currentColor"/></svg>';
/** An eye in bark: the face Ceda looks for. */
const FACE = '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="8" cy="8" rx="5.2" ry="6.4" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M5.4 6.6h1.6M9 6.6h1.6M6 10.6c1.2.7 2.8.7 4 0" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
/** Hob's cap and a cane. */
const HOB = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 7.5h10M4.2 7.5c0-2.6 1.7-4 3.8-4s3.8 1.4 3.8 4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M5 10.5c.8 2 5.2 2 6 0" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
/** A seed. */
const SEED = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.8c3 2.6 4.2 5.2 4.2 7.6A4.2 4.2 0 0 1 3.8 9.4C3.8 7 5 4.4 8 1.8z" fill="currentColor"/></svg>';
/** A root, branching. */
const ROOT = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5V8M8 8c-1.6 1.6-3.8 2.2-5.5 2M8 8c1.6 1.6 3.8 2.2 5.5 2M8 8v6.5M8 11.5c-1 .9-2.2 1.3-3.4 1.3" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
/** The Lodazal: a lump of mud with two eyes. */
const MUD = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 13c0-5 2.6-8.5 6-8.5s6 3.5 6 8.5z" fill="currentColor"/><circle cx="6" cy="9" r="1.1" fill="#0c0e12"/><circle cx="10" cy="9" r="1.1" fill="#0c0e12"/></svg>';

type Mark = { el: HTMLButtonElement; state: HTMLElement; node: number; point: THREE.Vector3 };

const count = (x: number): number => {
  let c = 0;
  for (let v = x; v; v &= v - 1) c++;
  return c;
};

/**
 * The Circle's wood on screen (docs/sendas/druida.md, chapter 1), inside a path's own plate (game/senda-hud.ts) and
 * over the board; a phone's plate kept short:
 *
 * - the thaw (`thaw`): where the hero stands (in the water, on a mound, in the mud, dry), the next surge (its seconds;
 *   red «¡Ruge arriba!» with its count while warned: get on a mound; «baja» while it comes down), the flood below.
 * - Olmo's clearing (`glade`): Ceda's search (its %), the shoots in pips (fenced white, eaten dark, alone green) and
 *   what Hob is doing; red when one is being eaten.
 * - the root bridge (`roots`, the test): the seeds left in pips, the roots on the islet (then the bridge braiding, its
 *   seconds), the Lodazal (down, bubbling under a shoot: hit it; its arm raised: get away; up on the bank), the mud
 *   coming (its seconds: get on a mound). Over the board, a mark on every stone (tap: throw a seed there), «brote» on
 *   the sown ones, and the knot's own mark, red, while there is one (tap: cut it).
 *
 * Plain DOM, written only when it changes (docs/RENDIMIENTO.md); the board's points from the sim's own rules.
 */
export class DruidHud {
  readonly panel: HTMLElement;
  private kind: 'thaw' | 'glade' | 'roots';
  private v = new THREE.Vector3();
  private key = '';
  private rows: Record<string, { row: HTMLElement; text: HTMLElement }> = {};
  private pips: HTMLElement[] = [];
  private marks: Mark[] = [];
  private knot: Mark | null = null;

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
  ) {
    const d = s.rules?.senda?.druid;
    this.kind = d?.kind ?? 'thaw';
    const rows: HTMLElement[] = [];
    const row = (id: string, ico: string, label = ''): HTMLElement => {
      const text = h('span.al-wave');
      const r = h(`div.bh-row.bq-${id}`, {}, h('span.bh-ico', { html: ico }), label ? h('span.bh-label', {}, label) : '', text);
      this.rows[id] = { row: r, text };
      rows.push(r);
      return r;
    };
    if (this.kind === 'thaw') {
      row('stand', MOUND);
      row('surge', WATER);
      row('low', WATER);
    } else if (this.kind === 'glade') {
      row('search', FACE, t('senda.bosque.search'));
      const shoots = row('shoots', SHOOT, t('senda.bosque.shoots'));
      this.pips = Array.from({ length: d?.glade?.shoots.length ?? 7 }, () => h('i'));
      shoots.append(h('span.al-pips.bq-pips', {}, ...this.pips));
      row('hob', HOB);
    } else if (d?.roots) {
      const seeds = row('seeds', SEED, t('senda.bosque.seeds'));
      this.pips = Array.from({ length: d.roots.seeds }, () => h('i'));
      seeds.append(h('span.al-pips.bq-seeds', {}, ...this.pips));
      row('roots', ROOT);
      row('boss', MUD);
      row('mud', WATER);
      this.buildMarks(s);
    }
    this.panel = h(`div.bh.bq-panel.bq-${this.kind}`, {}, ...rows);
  }

  /** A mark on every stone of the net, and the knot's. */
  private buildMarks(s: BattleState): void {
    const r = s.rules?.senda?.druid?.roots;
    if (!r) return;
    const mk = (node: number, knot: boolean): Mark => {
      const state = h('small.al-state.bq-state');
      const hint = t(knot ? 'senda.bosque.knotHint' : 'senda.bosque.stoneHint');
      const el = h(
        `button.al-stone.bq-stone${knot ? '.knot' : ''}`,
        { type: 'button', 'data-test': knot ? 'root-knot' : `root-stone-${node}`, 'aria-label': hint, title: hint, onpointerup: (e: Event) => (e.stopPropagation(), this.mark(knot ? KNOT : node)) },
        h('span.al-ring'),
        h('span.bq-glyph', { html: knot ? ROOT : SEED }),
        state,
      ) as HTMLButtonElement;
      this.board.append(el);
      const c = node >= 0 ? hexToWorld((r.nodes[node] as { q: number }).q, (r.nodes[node] as { r: number }).r) : { x: 0, y: 0 };
      return { el, state, node, point: simToWorld(c.x, c.y, new THREE.Vector3(), 0.55) };
    };
    for (let n = 3; n < r.nodes.length; n++) this.marks.push(mk(n, false));
    this.knot = mk(-1, true);
  }

  private set(id: string, text: string, cls?: 'warn' | 'good' | ''): void {
    const r = this.rows[id];
    if (!r) return;
    if (r.text.textContent !== text) r.text.textContent = text;
    r.row.classList.toggle('warn', cls === 'warn');
    r.row.classList.toggle('good', cls === 'good');
  }

  private updateThaw(s: BattleState): void {
    const r = s.rules?.senda?.druid?.thaw;
    const w = s.senda?.druid;
    const hero = s.heroes.find((x) => x.downTicks === 0) ?? s.heroes[0];
    if (!r || !w || !hero) return;
    const wet = thawWet(s, hero.x, hero.y);
    const on = onMound(s, hero.x, hero.y);
    const mud = !wet && !on && (s.rules?.senda?.druid?.thaw?.mud.length ?? 0) > 0 && this.inMud(s, hero.x, hero.y);
    this.set('stand', wet ? t('senda.bosque.inWater') : on ? t('senda.bosque.onMound') : mud ? t('senda.bosque.inMud') : t('senda.bosque.dry'), wet ? 'warn' : on ? 'good' : '');
    const warned = w.front === NONE && w.warned > w.surges;
    const left = Math.max(0, Math.ceil((w.next - s.waveT) / TICK_HZ));
    this.set('surge', w.front !== NONE ? t('senda.bosque.surgeDown') : warned ? t('senda.bosque.surgeWarn', { s: Math.max(1, left) }) : t('senda.bosque.surgeIn', { s: left }), w.front !== NONE || warned ? 'warn' : '');
    this.set('low', w.low !== NONE ? t('senda.bosque.flood') : t('senda.bosque.floodSoon'), w.low !== NONE && w.low - hero.y < 3500 ? 'warn' : '');
  }

  private inMud(s: BattleState, x: number, y: number): boolean {
    const r = s.rules?.senda?.druid?.thaw;
    if (!r) return false;
    for (const p of r.patches) if (Math.abs(p.x - x) + Math.abs(p.y - y) < 1700) return true;
    return false;
  }

  private updateGlade(s: BattleState): void {
    const r = s.rules?.senda?.druid?.glade;
    const w = s.senda?.druid;
    if (!r || !w) return;
    const k = Math.min(100, Math.floor((s.waveT * 100) / Math.max(1, r.search)));
    this.set('search', `${k} %`, w.done >= 0 ? 'good' : '');
    const eating = Object.keys(w.eating).length > 0;
    const f = count(w.fenced);
    const e = count(w.eaten);
    this.set('shoots', `${t('senda.bosque.fenced', { n: f })} · ${t('senda.bosque.eaten', { n: e })}`, eating ? 'warn' : '');
    // (The clearing keeps sprouting: a pip for each shoot up so far.)
    const up = gladeUp(r, s.waveT);
    this.pips.forEach((p, i) => {
      p.hidden = i >= up;
      p.classList.toggle('fenced', !!(w.fenced & (1 << i)));
      p.classList.toggle('eaten', !!(w.eaten & (1 << i)));
    });
    const hob = s.senda?.npcs.find((n) => n.id === 'hob');
    const hobText = !hob ? '' : hob.flinch > 0 ? t('senda.bosque.hobHit') : hob.spot >= r.shoots.length ? t('senda.bosque.hobDone') : hob.spot >= up && !hob.moving ? t('senda.bosque.hobWait') : hob.moving ? t('senda.bosque.hobWalk') : t('senda.bosque.hobFence');
    this.set('hob', eating ? t('senda.bosque.slimeEats') : hobText, eating || (hob?.flinch ?? 0) > 0 ? 'warn' : '');
  }

  private updateRoots(s: BattleState): void {
    const r = s.rules?.senda?.druid?.roots;
    const w = s.senda?.druid;
    const st = w?.roots;
    if (!r || !w || !st) return;
    this.set('seeds', String(w.seeds), w.seeds <= 3 ? 'warn' : '');
    this.pips.forEach((p, i) => p.classList.toggle('on', i < w.seeds));
    const growing = st.tips.some((x) => x.mode === 1);
    const rootsText =
      w.weave >= 0
        ? t('senda.bosque.weave', { s: Math.max(0, Math.ceil((w.weave + r.weave - s.tick) / TICK_HZ)) })
        : st.knot >= 0
          ? t('senda.bosque.knot')
          : growing
            ? t('senda.bosque.growing')
            : t('senda.bosque.waiting');
    this.set('roots', rootsText, st.knot >= 0 ? 'warn' : w.weave >= 0 ? 'good' : '');
    const boss = w.boss ? s.enemies.find((e) => e.id === w.boss && e.hp > 0) : undefined;
    const bossText = !boss ? t('senda.bosque.gone') : w.bmode === 1 || w.bmode === 2 ? t('senda.bosque.bubble') : w.bmode === 5 ? t('senda.bosque.slap') : w.bmode === 0 ? t('senda.bosque.deep') : t('senda.bosque.up');
    this.set('boss', bossText, w.bmode === 1 || w.bmode === 2 || w.bmode === 5 ? 'warn' : !boss ? 'good' : '');
    const mudRow = this.rows.mud;
    if (mudRow) mudRow.row.hidden = w.wave < 0;
    if (w.wave >= 0) this.set('mud', t('senda.bosque.mudIn', { s: Math.max(1, Math.ceil((w.wave - s.tick) / TICK_HZ)) }), 'warn');
    // The marks: sown or not, the one the hero was sent to, the knot.
    const key = `${st.sprout}|${st.knot}|${w.job}|${w.done}|${w.weave}`;
    if (key !== this.key) {
      this.key = key;
      for (const m of this.marks) {
        const sown = !!(st.sprout & (1 << m.node));
        const state = sown ? t('senda.bosque.sprouted') : '';
        if (m.state.textContent !== state) {
          m.state.textContent = state;
          remeasureMark(m.el);
        }
        m.el.classList.toggle('sown', sown);
        m.el.classList.toggle('gone', w.weave >= 0 || w.done >= 0);
      }
      if (this.knot) {
        this.knot.el.classList.toggle('gone', st.knot < 0);
        if (st.knot >= 0) {
          const c = hexToWorld((r.nodes[st.knot] as { q: number }).q, (r.nodes[st.knot] as { r: number }).r);
          simToWorld(c.x, c.y, this.knot.point, 0.9);
        }
      }
    }
    const sent = w.job;
    for (const m of [...this.marks, ...(this.knot ? [this.knot] : [])]) {
      const on = m === this.knot ? sent === -2 : sent === m.node;
      m.el.classList.toggle('sent', on);
      m.el.style.setProperty('--k', on ? Math.min(1, w.held / Math.max(1, sent === -2 ? r.cut : r.throw)).toFixed(2) : '0');
      m.el.disabled = s.result !== 'running';
    }
  }

  update(s: BattleState): void {
    if (this.kind === 'thaw') this.updateThaw(s);
    else if (this.kind === 'glade') this.updateGlade(s);
    else this.updateRoots(s);
  }

  /** Every frame: the marks over their places. */
  place(camera: THREE.Camera, w: number, ht: number): void {
    for (const m of [...this.marks, ...(this.knot ? [this.knot] : [])]) {
      if (m.el.classList.contains('gone')) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      this.v.copy(m.point).project(camera);
      if (this.v.z > 1 || this.v.x < -1.1 || this.v.x > 1.1 || this.v.y < -1.1 || this.v.y > 1.1) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      setStyle(m.el, 'display', '');
      pinMark(m.el, Math.min(w - 40, Math.max(40, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht);
    }
  }
}
